import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock3,
  IndianRupee,
  ReceiptText,
  Search,
  Wallet,
} from "lucide-react";

import {
  api,
} from "@/lib/api";

import {
  toast,
} from "sonner";

import {
  PageHeader,
} from "@/components/PageHeader";

import {
  Card,
  Input,
  Select,
  Empty,
  Btn,
} from "@/components/ui-kit";


const PAYMENT_MODES = [
  ["bank_transfer", "Bank Transfer"],
  ["upi", "UPI"],
  ["cheque", "Cheque"],
  ["cash", "Cash"],
  ["neft", "NEFT"],
  ["rtgs", "RTGS"],
  ["imps", "IMPS"],
  ["payment_gateway", "Payment Gateway"],
  ["other", "Other"],
];


const inr = (v) =>
  Number(v || 0).toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );


const prettyMonth = (value) => {
  if (!value) return "—";

  const [y, m] =
    value.split("-").map(Number);

  if (!y || !m) return value;

  return new Date(
    y,
    m - 1,
    1
  ).toLocaleDateString(
    "en-IN",
    {
      month: "short",
      year: "numeric",
    }
  );
};


const dateOnly = (value) => {
  if (!value) return "—";

  return String(value).slice(
    0,
    10
  );
};


const daysDifference = (
  date
) => {
  if (!date) return 0;

  const due =
    new Date(`${dateOnly(date)}T00:00:00`);

  const today =
    new Date();

  today.setHours(
    0,
    0,
    0,
    0
  );

  return Math.floor(
    (
      today.getTime()
      - due.getTime()
    )
    / 86400000
  );
};


const agingBucket = (
  invoice
) => {
  const pending =
    Number(
      invoice.balance_due
      ??
      invoice.pending_amount
      ??
      invoice.grand_total
      ??
      0
    );

  if (pending <= 0) {
    return "Paid";
  }

  const overdueDays =
    daysDifference(
      invoice.due_date
    );

  if (overdueDays <= 0) {
    return "Current";
  }

  if (overdueDays <= 30) {
    return "1–30 Days";
  }

  if (overdueDays <= 60) {
    return "31–60 Days";
  }

  if (overdueDays <= 90) {
    return "61–90 Days";
  }

  return "90+ Days";
};


const paymentStatus = (
  invoice
) => {
  const pending =
    Number(
      invoice.balance_due
      ??
      invoice.pending_amount
      ??
      invoice.grand_total
      ??
      0
    );

  const received =
    Number(
      invoice.paid_amount
      || 0
    );

  if (
    invoice.status ===
    "cancelled"
  ) {
    return "Cancelled";
  }

  if (
    invoice.status ===
    "draft"
  ) {
    return "Not Raised";
  }

  if (pending <= 0) {
    return "Paid";
  }

  if (
    invoice.status ===
    "overdue"
  ) {
    return "Overdue";
  }

  if (received > 0) {
    return "Partially Paid";
  }

  return "Unpaid";
};


const paymentBadgeClass = (
  label
) => {
  switch (label) {
    case "Paid":
      return (
        "bg-emerald-50 " +
        "text-emerald-700 " +
        "border-emerald-200"
      );

    case "Partially Paid":
      return (
        "bg-amber-50 " +
        "text-amber-700 " +
        "border-amber-200"
      );

    case "Overdue":
      return (
        "bg-rose-50 " +
        "text-rose-700 " +
        "border-rose-200"
      );

    case "Unpaid":
      return (
        "bg-orange-50 " +
        "text-orange-700 " +
        "border-orange-200"
      );

    case "Not Raised":
      return (
        "bg-slate-50 " +
        "text-slate-600 " +
        "border-slate-200"
      );

    default:
      return (
        "bg-slate-50 " +
        "text-slate-600 " +
        "border-slate-200"
      );
  }
};


const agingClass = (
  bucket
) => {
  switch (bucket) {
    case "Current":
      return (
        "bg-blue-50 " +
        "text-blue-700"
      );

    case "1–30 Days":
      return (
        "bg-amber-50 " +
        "text-amber-700"
      );

    case "31–60 Days":
      return (
        "bg-orange-50 " +
        "text-orange-700"
      );

    case "61–90 Days":
      return (
        "bg-rose-50 " +
        "text-rose-700"
      );

    case "90+ Days":
      return (
        "bg-red-100 " +
        "text-red-800"
      );

    default:
      return (
        "bg-slate-50 " +
        "text-slate-600"
      );
  }
};


function Kpi({
  title,
  value,
  icon: Icon,
  note,
  tone,
}) {
  return (
    <Card
      className={
        `p-3.5 ${tone}`
      }
    >
      <div className="flex items-center gap-3">

        <div
          className={
            "w-9 h-9 rounded-xl " +
            "bg-white/80 flex " +
            "items-center justify-center " +
            "shrink-0"
          }
        >
          <Icon
            size={18}
          />
        </div>

        <div className="min-w-0">

          <div className="nk-overline">
            {title}
          </div>

          <div
            className={
              "nk-num text-lg " +
              "font-bold text-slate-900 " +
              "truncate"
            }
          >
            {value}
          </div>

          <div
            className={
              "text-[10px] " +
              "text-slate-500"
            }
          >
            {note}
          </div>

        </div>

      </div>
    </Card>
  );
}


export default function Receivables() {

  const navigate =
    useNavigate();

  const [
    searchParams,
  ] = useSearchParams();


  const queryMonth =
    searchParams.get(
      "month"
    ) || "";

  const queryClient =
    searchParams.get(
      "client_id"
    ) || "";


  const [
    invoices,
    setInvoices,
  ] = useState([]);

  const [
    clients,
    setClients,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    payment,
    setPayment,
  ] = useState(null);

  const [
    paymentForm,
    setPaymentForm,
  ] = useState({
    amount: "",
    receipt_date:
      new Date()
        .toISOString()
        .slice(0, 10),
    payment_mode:
      "bank_transfer",
    reference_no: "",
    notes: "",
  });


  const [
    search,
    setSearch,
  ] = useState("");

  const [
    month,
    setMonth,
  ] = useState(
    queryMonth
  );

  const [
    clientId,
    setClientId,
  ] = useState(
    queryClient
  );

  const [
    status,
    setStatus,
  ] = useState(
    "all"
  );

  const [
    aging,
    setAging,
  ] = useState(
    "all"
  );


  useEffect(() => {
    let mounted = true;

    Promise.all([
      api.get("/invoices"),
      api.get("/clients"),
    ])
      .then(
        ([
          invoiceResponse,
          clientResponse,
        ]) => {
          if (!mounted) {
            return;
          }

          setInvoices(
            invoiceResponse.data
            || []
          );

          setClients(
            clientResponse.data
            || []
          );
        }
      )
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);


  const reloadInvoices = async () => {
    const response =
      await api.get("/invoices");

    setInvoices(
      response.data || []
    );
  };


  const openPayment = async (
    invoice
  ) => {
    if (
      invoice.status === "draft"
      ||
      invoice.status === "cancelled"
      ||
      invoice.pending <= 0
    ) {
      return;
    }

    try {
      const response =
        await api.get(
          `/invoices/${invoice.id}`
        );

      setPayment({
        invoice:
          response.data.invoice
          || invoice,

        payment_summary:
          response.data.payment_summary
          || {},

        receipts:
          response.data.receipts
          || [],
      });

      setPaymentForm({
        amount: "",
        receipt_date:
          new Date()
            .toISOString()
            .slice(0, 10),
        payment_mode:
          "bank_transfer",
        reference_no: "",
        notes: "",
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.detail
        || "Unable to load payment details"
      );
    }
  };


  const savePayment = async () => {
    if (!payment?.invoice) {
      return;
    }

    const amount =
      Number(
        paymentForm.amount
      );

    const pending =
      Number(
        payment.payment_summary
          ?.pending_amount
        ??
        payment.invoice
          ?.balance_due
        ??
        payment.invoice
          ?.pending_amount
        ??
        payment.invoice
          ?.grand_total
        ??
        0
      );

    if (
      !amount
      ||
      amount <= 0
    ) {
      toast.error(
        "Enter a valid received amount"
      );
      return;
    }

    if (amount > pending) {
      toast.error(
        `Amount cannot exceed pending balance ₹${inr(pending)}`
      );
      return;
    }

    try {
      await api.post(
        `/clients/${payment.invoice.client_id}/receipts`,
        {
          invoice_id:
            payment.invoice.id,

          amount,

          receipt_date:
            paymentForm.receipt_date,

          payment_mode:
            paymentForm.payment_mode,

          reference_no:
            paymentForm.reference_no
            || null,

          notes:
            paymentForm.notes
            || null,
        }
      );

      toast.success(
        "Payment recorded successfully"
      );

      setPayment(null);

      await reloadInvoices();

    } catch (error) {
      toast.error(
        error?.response?.data?.detail
        || "Unable to record payment"
      );
    }
  };


  const cname = (id) =>
    clients.find(
      (c) =>
        c.id === id
    )?.name || "—";


  const months =
    useMemo(
      () =>
        [
          ...new Set(
            invoices
              .map(
                (i) =>
                  i.month
              )
              .filter(Boolean)
          ),
        ]
          .sort()
          .reverse(),
      [
        invoices,
      ]
    );


  const rows =
    useMemo(
      () => {

        return invoices
          .map(
            (invoice) => {

              const pending =
                Number(
                  invoice.balance_due
                  ??
                  invoice.pending_amount
                  ??
                  invoice.grand_total
                  ??
                  0
                );

              const received =
                Number(
                  invoice.paid_amount
                  || 0
                );

              const pStatus =
                paymentStatus(
                  invoice
                );

              const bucket =
                agingBucket(
                  invoice
                );

              const overdueDays =
                Math.max(
                  0,
                  daysDifference(
                    invoice.due_date
                  )
                );

              return {
                ...invoice,

                client_name:
                  cname(
                    invoice.client_id
                  ),

                pending,

                received,

                payment_status:
                  pStatus,

                aging_bucket:
                  bucket,

                overdue_days:
                  overdueDays,
              };
            }
          )
          .filter(
            (invoice) => {

              if (
                invoice.status ===
                "cancelled"
              ) {
                return false;
              }

              if (
                month &&
                invoice.month !==
                  month
              ) {
                return false;
              }

              if (
                clientId &&
                clientId !==
                  "all" &&
                invoice.client_id !==
                  clientId
              ) {
                return false;
              }

              if (
                status !==
                "all" &&
                invoice.payment_status !==
                  status
              ) {
                return false;
              }

              if (
                aging !==
                "all" &&
                invoice.aging_bucket !==
                  aging
              ) {
                return false;
              }

              const q =
                search
                  .trim()
                  .toLowerCase();

              if (q) {
                const haystack = [
                  invoice.number,
                  invoice.client_name,
                  invoice.month,
                  invoice.payment_status,
                ]
                  .join(" ")
                  .toLowerCase();

                if (
                  !haystack.includes(
                    q
                  )
                ) {
                  return false;
                }
              }

              return true;
            }
          );
      },
      [
        invoices,
        clients,
        month,
        clientId,
        status,
        aging,
        search,
      ]
    );


  const summary =
    useMemo(
      () => {

        let totalReceivable = 0;
        let overdueAmount = 0;
        let received = 0;
        let partial = 0;
        let dueSoon = 0;

        rows.forEach(
          (r) => {

            totalReceivable +=
              r.pending;

            received +=
              r.received;

            if (
              r.payment_status ===
              "Overdue"
            ) {
              overdueAmount +=
                r.pending;
            }

            if (
              r.payment_status ===
              "Partially Paid"
            ) {
              partial += 1;
            }

            const dueDiff =
              -daysDifference(
                r.due_date
              );

            if (
              r.pending > 0 &&
              dueDiff >= 0 &&
              dueDiff <= 7
            ) {
              dueSoon += 1;
            }
          }
        );

        return {
          totalReceivable,
          overdueAmount,
          received,
          partial,
          dueSoon,
        };
      },
      [
        rows,
      ]
    );


  const openInvoice = (
    invoice
  ) => {

    const params =
      new URLSearchParams();

    if (invoice.month) {
      params.set(
        "month",
        invoice.month
      );
    }

    if (invoice.client_id) {
      params.set(
        "client_id",
        invoice.client_id
      );
    }

    navigate(
      `/invoices?${params.toString()}`
    );
  };


  if (loading) {
    return (
      <div className="p-5 text-sm text-slate-500">
        Loading receivables...
      </div>
    );
  }


  return (
    <div>

      <PageHeader
        title="Receivables"
        subtitle={
          "Track client collections, pending balances and overdue invoices"
        }
      />


      <div
        className={
          "grid grid-cols-2 " +
          "lg:grid-cols-5 " +
          "gap-3 mb-4"
        }
      >

        <Kpi
          title="Total Receivable"
          value={
            `₹${inr(
              summary.totalReceivable
            )}`
          }
          icon={IndianRupee}
          note="Pending collection"
          tone="bg-blue-50"
        />

        <Kpi
          title="Overdue Amount"
          value={
            `₹${inr(
              summary.overdueAmount
            )}`
          }
          icon={AlertTriangle}
          note="Past due balance"
          tone="bg-rose-50"
        />

        <Kpi
          title="Total Received"
          value={
            `₹${inr(
              summary.received
            )}`
          }
          icon={CheckCircle2}
          note="Collected against invoices"
          tone="bg-emerald-50"
        />

        <Kpi
          title="Partially Paid"
          value={
            summary.partial
          }
          icon={Wallet}
          note="Open partial invoices"
          tone="bg-amber-50"
        />

        <Kpi
          title="Due Soon"
          value={
            summary.dueSoon
          }
          icon={CalendarClock}
          note="Due within 7 days"
          tone="bg-violet-50"
        />

      </div>


      <Card className="p-4 mb-4">

        <div
          className={
            "grid grid-cols-1 " +
            "md:grid-cols-2 " +
            "xl:grid-cols-5 gap-3"
          }
        >

          <div className="relative">

            <Search
              size={15}
              className={
                "absolute left-3 " +
                "top-1/2 -translate-y-1/2 " +
                "text-slate-400"
              }
            />

            <Input
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
              placeholder={
                "Search client or invoice..."
              }
              className="pl-9"
            />

          </div>


          <Select
            value={month}
            onChange={(e) =>
              setMonth(
                e.target.value
              )
            }
          >
            <option value="">
              All Months
            </option>

            {months.map(
              (m) => (
                <option
                  key={m}
                  value={m}
                >
                  {prettyMonth(m)}
                </option>
              )
            )}
          </Select>


          <Select
            value={clientId}
            onChange={(e) =>
              setClientId(
                e.target.value
              )
            }
          >
            <option value="">
              All Clients
            </option>

            {clients.map(
              (c) => (
                <option
                  key={c.id}
                  value={c.id}
                >
                  {c.name}
                </option>
              )
            )}
          </Select>


          <Select
            value={status}
            onChange={(e) =>
              setStatus(
                e.target.value
              )
            }
          >
            <option value="all">
              All Payment Statuses
            </option>

            <option value="Not Raised">
              Not Raised
            </option>

            <option value="Unpaid">
              Unpaid
            </option>

            <option value="Partially Paid">
              Partially Paid
            </option>

            <option value="Overdue">
              Overdue
            </option>

            <option value="Paid">
              Paid
            </option>

          </Select>


          <Select
            value={aging}
            onChange={(e) =>
              setAging(
                e.target.value
              )
            }
          >
            <option value="all">
              All Aging
            </option>

            <option value="Current">
              Current
            </option>

            <option value="1–30 Days">
              1–30 Days
            </option>

            <option value="31–60 Days">
              31–60 Days
            </option>

            <option value="61–90 Days">
              61–90 Days
            </option>

            <option value="90+ Days">
              90+ Days
            </option>

          </Select>

        </div>

      </Card>


      <Card className="overflow-hidden">

        <div className="overflow-x-auto">

          {rows.length ? (

            <table className="nk-table">

              <thead>
                <tr>
                  <th>Client</th>
                  <th>Invoice No.</th>
                  <th>Month</th>
                  <th>Invoice Date</th>
                  <th>Due Date</th>
                  <th>Grand Total</th>
                  <th>Received</th>
                  <th>Pending</th>
                  <th>Payment Status</th>
                  <th>Aging</th>
                  <th>Last Payment</th>
                  <th></th>
                </tr>
              </thead>


              <tbody>

                {rows.map(
                  (r) => (

                    <tr
                      key={r.id}
                    >

                      <td className="font-semibold">
                        {r.client_name}
                      </td>


                      <td>
                        <button
                          type="button"
                          onClick={() =>
                            openInvoice(r)
                          }
                          className={
                            "text-blue-700 " +
                            "font-semibold " +
                            "hover:underline"
                          }
                        >
                          {r.number}
                        </button>
                      </td>


                      <td>
                        {prettyMonth(
                          r.month
                        )}
                      </td>


                      <td>
                        {dateOnly(
                          r.invoice_date
                          ||
                          r.created_at
                        )}
                      </td>


                      <td>
                        {dateOnly(
                          r.due_date
                        )}
                      </td>


                      <td className="nk-num font-semibold">
                        ₹{inr(
                          r.grand_total
                        )}
                      </td>


                      <td
                        className={
                          "nk-num " +
                          "font-semibold " +
                          "text-emerald-700"
                        }
                      >
                        ₹{inr(
                          r.received
                        )}
                      </td>


                      <td
                        className={
                          "nk-num " +
                          "font-bold " +
                          (
                            r.pending > 0
                              ? "text-rose-700"
                              : "text-emerald-700"
                          )
                        }
                      >
                        ₹{inr(
                          r.pending
                        )}
                      </td>


                      <td>

                        <span
                          className={
                            "inline-flex items-center " +
                            "px-2 py-1 rounded-full " +
                            "border text-[10px] " +
                            "font-semibold " +
                            paymentBadgeClass(
                              r.payment_status
                            )
                          }
                        >
                          {r.payment_status}
                        </span>

                      </td>


                      <td>

                        <span
                          className={
                            "inline-flex items-center " +
                            "px-2 py-1 rounded-full " +
                            "text-[10px] font-semibold " +
                            agingClass(
                              r.aging_bucket
                            )
                          }
                        >
                          {r.aging_bucket}
                        </span>

                        {r.overdue_days > 0 && (
                          <div
                            className={
                              "text-[9px] " +
                              "text-slate-400 mt-1"
                            }
                          >
                            {r.overdue_days} days overdue
                          </div>
                        )}

                      </td>


                      <td>
                        {r.last_payment_date
                          || "—"}
                      </td>


                      <td className="text-right">

                        <div className="flex justify-end gap-2">

                          {[
                            "raised",
                            "submitted",
                            "partially_paid",
                            "overdue",
                          ].includes(
                            r.status
                          )
                          &&
                          r.pending > 0 && (

                            <Btn
                              onClick={() =>
                                openPayment(r)
                              }
                            >
                              <IndianRupee
                                size={14}
                              />
                              Record Payment
                            </Btn>

                          )}

                          <Btn
                            variant="outline"
                            onClick={() =>
                              openInvoice(r)
                            }
                          >
                            <ReceiptText
                              size={14}
                            />
                            Invoice
                          </Btn>

                        </div>

                      </td>

                    </tr>
                  )
                )}

              </tbody>

            </table>

          ) : (

            <Empty
              title="No receivables found"
              hint={
                "Try changing the month, client, status or aging filters."
              }
            />

          )}

        </div>

      </Card>


      <div
        className={
          "mt-3 flex items-center " +
          "gap-2 text-[10px] " +
          "text-slate-400"
        }
      >
        <Clock3 size={13} />

        Payment status is calculated from
        recorded receipts and invoice due dates.
      </div>


      {payment && (

        <div
          className={
            "fixed inset-0 z-50 " +
            "bg-slate-950/40 " +
            "flex items-center justify-center " +
            "p-4"
          }
        >

          <div
            className={
              "bg-white rounded-2xl shadow-2xl " +
              "w-full max-w-xl max-h-[90vh] " +
              "overflow-y-auto"
            }
          >

            <div
              className={
                "px-5 py-4 border-b " +
                "flex items-start justify-between gap-3"
              }
            >

              <div>

                <div
                  className={
                    "text-lg font-bold text-slate-900"
                  }
                >
                  Record Payment
                </div>

                <div
                  className={
                    "text-xs text-slate-500 mt-1"
                  }
                >
                  {payment.invoice.number}
                  {" · "}
                  {cname(
                    payment.invoice.client_id
                  )}
                </div>

              </div>

              <button
                type="button"
                onClick={() =>
                  setPayment(null)
                }
                className={
                  "text-slate-400 hover:text-slate-700"
                }
              >
                ✕
              </button>

            </div>


            <div className="p-5">

              <div
                className={
                  "grid grid-cols-3 gap-3 mb-5"
                }
              >

                <div
                  className={
                    "rounded-xl bg-slate-50 p-3"
                  }
                >
                  <div className="nk-overline">
                    Grand Total
                  </div>

                  <div
                    className={
                      "nk-num font-bold mt-1"
                    }
                  >
                    ₹{inr(
                      payment.invoice.grand_total
                    )}
                  </div>
                </div>


                <div
                  className={
                    "rounded-xl bg-emerald-50 p-3"
                  }
                >
                  <div className="nk-overline">
                    Received
                  </div>

                  <div
                    className={
                      "nk-num font-bold text-emerald-700 mt-1"
                    }
                  >
                    ₹{inr(
                      payment.payment_summary
                        ?.received_amount
                      ??
                      payment.invoice
                        ?.paid_amount
                      ??
                      0
                    )}
                  </div>
                </div>


                <div
                  className={
                    "rounded-xl bg-rose-50 p-3"
                  }
                >
                  <div className="nk-overline">
                    Pending
                  </div>

                  <div
                    className={
                      "nk-num font-bold text-rose-700 mt-1"
                    }
                  >
                    ₹{inr(
                      payment.payment_summary
                        ?.pending_amount
                      ??
                      payment.invoice
                        ?.balance_due
                      ??
                      payment.invoice
                        ?.pending_amount
                      ??
                      payment.invoice
                        ?.grand_total
                    )}
                  </div>
                </div>

              </div>


              <div
                className={
                  "grid grid-cols-1 md:grid-cols-2 gap-4"
                }
              >

                <div>
                  <label className="nk-overline block mb-1.5">
                    Amount Received *
                  </label>

                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={
                      paymentForm.amount
                    }
                    onChange={(e) =>
                      setPaymentForm(
                        (prev) => ({
                          ...prev,
                          amount:
                            e.target.value,
                        })
                      )
                    }
                    placeholder="Enter actual amount received"
                  />
                </div>


                <div>
                  <label className="nk-overline block mb-1.5">
                    Receipt Date *
                  </label>

                  <Input
                    type="date"
                    value={
                      paymentForm.receipt_date
                    }
                    onChange={(e) =>
                      setPaymentForm(
                        (prev) => ({
                          ...prev,
                          receipt_date:
                            e.target.value,
                        })
                      )
                    }
                  />
                </div>


                <div>
                  <label className="nk-overline block mb-1.5">
                    Payment Mode *
                  </label>

                  <Select
                    value={
                      paymentForm.payment_mode
                    }
                    onChange={(e) =>
                      setPaymentForm(
                        (prev) => ({
                          ...prev,
                          payment_mode:
                            e.target.value,
                        })
                      )
                    }
                  >
                    {PAYMENT_MODES.map(
                      ([
                        value,
                        label,
                      ]) => (
                        <option
                          key={value}
                          value={value}
                        >
                          {label}
                        </option>
                      )
                    )}
                  </Select>
                </div>


                <div>
                  <label className="nk-overline block mb-1.5">
                    Reference / Transaction No.
                  </label>

                  <Input
                    value={
                      paymentForm.reference_no
                    }
                    onChange={(e) =>
                      setPaymentForm(
                        (prev) => ({
                          ...prev,
                          reference_no:
                            e.target.value,
                        })
                      )
                    }
                    placeholder="UTR / Cheque / Gateway reference"
                  />
                </div>

              </div>


              <div className="mt-4">

                <label className="nk-overline block mb-1.5">
                  Notes
                </label>

                <textarea
                  value={
                    paymentForm.notes
                  }
                  onChange={(e) =>
                    setPaymentForm(
                      (prev) => ({
                        ...prev,
                        notes:
                          e.target.value,
                      })
                    )
                  }
                  rows={3}
                  className={
                    "w-full rounded-xl border border-slate-200 " +
                    "px-3 py-2 text-sm outline-none " +
                    "focus:ring-2 focus:ring-blue-100 " +
                    "focus:border-blue-400"
                  }
                  placeholder="Optional payment notes"
                />

              </div>


              <div
                className={
                  "mt-5 flex justify-end gap-2"
                }
              >

                <Btn
                  variant="outline"
                  onClick={() =>
                    setPayment(null)
                  }
                >
                  Cancel
                </Btn>

                <Btn
                  onClick={
                    savePayment
                  }
                >
                  <IndianRupee
                    size={14}
                  />
                  Save Payment
                </Btn>

              </div>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}
